import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';

export function navigateToCustomerSearchResult(navigate, result) {
  switch (result?.type) {
    case 'job':
      navigate(`/appointments/${result.id}`);
      break;
    case 'claim':
      navigate(`/claims/${result.id}`);
      break;
    case 'claimant':
      navigate(`/claimants?claimantId=${result.id}`);
      break;
    default:
      break;
  }
}

const getResultIcon = (type) => {
  switch (type) {
    case 'job':
      return '💼';
    case 'claimant':
      return '👤';
    case 'claim':
      return '📋';
    default:
      return '📄';
  }
};

const GlobalSearch = () => {
  const { makeAuthenticatedRequest } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  const performSearch = async (searchQuery) => {
    const trimmed = searchQuery.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setShowDropdown(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await makeAuthenticatedRequest(
        `/global-search?q=${encodeURIComponent(trimmed)}`
      );
      if (data?.success) {
        setResults(data.data || []);
        setShowDropdown(true);
      } else {
        setResults([]);
        setShowDropdown(true);
      }
    } catch (error) {
      console.error('Customer global search error:', error);
      setResults([]);
      setShowDropdown(true);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const nextQuery = e.target.value;
    setQuery(nextQuery);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      performSearch(nextQuery);
    }, 300);
  };

  const handleResultClick = (result) => {
    setShowDropdown(false);
    setQuery('');
    setResults([]);
    navigateToCustomerSearchResult(navigate, result);
  };

  const handleSubmit = (e) => {
    if (e.key === 'Enter' && query.trim()) {
      e.preventDefault();
      setShowDropdown(false);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
    }
  };

  return (
    <div className="relative w-full max-w-xl mx-auto">
      <div className="relative">
        <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search jobs, claimants, claim numbers, phones..."
          value={query}
          onChange={handleChange}
          onKeyDown={handleSubmit}
          onFocus={() => query.trim().length >= 2 && setShowDropdown(true)}
          onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
          className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
          </div>
        )}
      </div>

      {showDropdown && results.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-96 overflow-auto">
          {results.map((result, index) => (
            <div
              key={`${result.type}-${result.id}-${index}`}
              className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-b-0"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleResultClick(result)}
            >
              <div className="flex items-center space-x-3">
                <span className="text-lg">{getResultIcon(result.type)}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-2">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {result.title}
                    </p>
                    <span className="px-2 py-1 text-xs font-medium bg-gray-100 text-gray-600 rounded">
                      {result.type.replace('_', ' ')}
                    </span>
                  </div>
                  {result.subtitle && (
                    <p className="text-sm text-gray-500 truncate">
                      {result.subtitle}
                    </p>
                  )}
                  {result.metadata && (
                    <p className="text-xs text-gray-400 truncate">
                      {result.metadata}
                    </p>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {showDropdown && query.trim().length >= 2 && results.length === 0 && !loading && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg p-4">
          <p className="text-sm text-gray-500 text-center">No results found</p>
        </div>
      )}
    </div>
  );
};

export default GlobalSearch;
